import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getCurrentWeather, getForecast, getRadar, getHourlyForecast, getAlerts, getAlertDetail } from '../services/weatherApiReact';
import WeatherCurrent from './WeatherCurrent';
import WeatherForecast from './WeatherForecast';
import WeatherMap from './WeatherMap';
import HourlyForecast from './HourlyForecast';
import TodayForecast from './TodayForecast';
import WeatherAlerts from './WeatherAlerts';
import './WeatherWidget.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/free-solid-svg-icons';

// Log the raw window object immediately to see its state before our component initializes further.
console.log('WeatherWidget: RAW window.ohtWeatherCachedData BEFORE initialGlobalCachedData definition:', 
    typeof window !== 'undefined' && window.ohtWeatherCachedData ? 
    JSON.parse(JSON.stringify(window.ohtWeatherCachedData)) : // Deep copy for logging to avoid later modifications affecting the log
    'window.ohtWeatherCachedData is not available or undefined'
);

const initialGlobalCachedData = (typeof window !== 'undefined' && window.ohtWeatherCachedData) ? 
                                window.ohtWeatherCachedData : 
                                { 
                                  current: null, 
                                  hourly: null, 
                                  forecast: null, 
                                  alerts: null, 
                                  location: null, 
                                  settings: { preload_main_widget: '0' },
                                  default_zipcode_for_preload: null 
                                };

console.log('WeatherWidget: Initial globalCachedData AFTER definition (full object):', initialGlobalCachedData);
console.log('WeatherWidget: CHECKING initialGlobalCachedData.location directly from the constructed object:', initialGlobalCachedData.location);

const WeatherWidget = ({
  zipcode: initialZipcode,
  locationName: initialLocationName,
  showTabs: initialShowTabs = ['hourly', 'map', 'upcoming'],
  showTodayForecast: initialShowTodayForecast = true,
  showCurrentWeather: initialShowCurrentWeather = true,
  showSearchBox: initialShowSearchBox = true,
  showLocation: initialShowLocation = true,
  showBorder: initialShowBorder = true,
  showAlerts: initialShowAlerts = true,
  linkUrl: initialLinkUrl = '',
}) => {
  const weatherRootRef = useRef(null);

  const [location, setLocation] = useState(initialLocationName || 'Loading...');
  const [currentWeather, setCurrentWeather] = useState(null);
  const [hourlyForecast, setHourlyForecast] = useState(null);
  const [forecast, setForecast] = useState(null); 
  const [todayForecast, setTodayForecast] = useState(null); 
  const [alerts, setAlerts] = useState(null);
  
  const [loading, setLoading] = useState(true); // Start with loading true
  const [error, setError] = useState(null);
  const [userSelectedTab, setUserSelectedTab] = useState(initialShowTabs[0] || 'hourly');
  const [searchZipcode, setSearchZipcode] = useState('');

  // activeTab is now derived from props and state, preventing spinner on hide
  let activeTab = userSelectedTab;
  if (initialShowTabs && !initialShowTabs.includes(activeTab)) {
    activeTab = initialShowTabs[0] || '';
  }

  // ===================================================================================
  // DEBUG: Log when the component renders and what the searchZipcode state is.
  // console.log(`DEBUG: WeatherWidget rendering, searchZipcode state is: "${searchZipcode}"`);

  // DEBUG: Log whenever the searchZipcode state is changed.
  useEffect(() => {
    // console.log(`DEBUG: searchZipcode state was set to: "${searchZipcode}"`);
  }, [searchZipcode]);
  // ===================================================================================

  const [mapLoaded, setMapLoaded] = useState(false);
  const [alertDetail, setAlertDetail] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [currentZipcode, setCurrentZipcode] = useState(initialZipcode); 
  const [linkUrl, setLinkUrl] = useState(initialLinkUrl);
  
  // UI display settings are now controlled by props
  const [showTodayForecast, setShowTodayForecast] = useState(initialShowTodayForecast);
  const [showCurrentWeather, setShowCurrentWeather] = useState(initialShowCurrentWeather);
  const [showSearchBox, setShowSearchBox] = useState(initialShowSearchBox);
  const [showLocationHeader, setShowLocationHeader] = useState(initialShowLocation);
  const [showBorder, setShowBorder] = useState(initialShowBorder);
  const [showAlertsUI, setShowAlertsUI] = useState(initialShowAlerts);
  
  const weatherMapRef = useRef(null);
  
  // Parse current weather data
  const parseCurrentWeatherData = useCallback((data, zipcode) => {
    if (data && (data.current || data.observation) && data.location) { // Ensure data.location exists too
      const obs = data.current?.current || data.current || data.observation;
      const loc = data.location; // data.location should be initialGlobalCachedData.location when preloading
      
      // console.log('WeatherWidget: parseCurrentWeatherData - Received loc for location name:', loc); // Log the loc object

      const locationDisplayName = `${loc.city || ''}, ${loc.stateCode || loc.adminDistrictCode || ''}`.trim().replace(/^, |, $/, '');
      if (locationDisplayName) {
        setLocation(locationDisplayName); 
        localStorage.setItem('weather_location', locationDisplayName);
      } else if (loc.postalCode) {
         setLocation(loc.postalCode); // Fallback to postal code if city/state are empty but postalCode exists
         localStorage.setItem('weather_location', loc.postalCode);
      } else if (zipcode) {
         setLocation(zipcode); // Fallback to the passed zipcode if all else fails
         localStorage.setItem('weather_location', zipcode);
      } else {
         // If no location info at all, keep existing or set to a generic default
         // setLocation('Weather Location');
      }

      const currentWeatherData = {
        temperature: obs.temperature !== undefined ? obs.temperature : 0,
        condition: (obs.wxPhraseLong || obs.wxPhraseShort || ''),
        humidity: obs.relativeHumidity !== undefined ? obs.relativeHumidity : 0, 
        wind: (obs.windDirectionCardinal ? obs.windDirectionCardinal + ' ' : '') + (obs.windSpeed ? obs.windSpeed + ' mph' : ''),
        windSpeed: obs.windSpeed !== undefined ? obs.windSpeed : 0,
        windDirection: obs.windDirectionCardinal || '',
        windChill: obs.temperatureWindChill !== undefined ? obs.temperatureWindChill : obs.temperature || 0,
        feelsLike: obs.temperatureFeelsLike !== undefined ? obs.temperatureFeelsLike : obs.temperature || 0,
        dewPoint: obs.temperatureDewPoint !== undefined ? obs.temperatureDewPoint : 0,
        visibility: obs.visibility !== undefined ? obs.visibility : '', 
        sunrise: obs.sunriseTimeLocal ? formatTime(obs.sunriseTimeLocal) : '',
        sunset: obs.sunsetTimeLocal ? formatTime(obs.sunsetTimeLocal) : '',
        uvIndex: obs.uvIndex !== undefined ? obs.uvIndex : 'N/A', 
        heatIndex: obs.temperatureHeatIndex !== undefined ? obs.temperatureHeatIndex : obs.temperature || 0,
        // Ensure all fields expected by WeatherCurrent are here
        icon: obs.iconCode || null 
      };
      
      // console.log('WeatherWidget: parseCurrentWeatherData - Constructed currentWeatherData for display:', currentWeatherData);
      
      setCurrentWeather(currentWeatherData);
      
      if (zipcode) { 
        setCurrentZipcode(zipcode);
        localStorage.setItem('last_weather_zipcode', zipcode);
      }
      
      return { parsedObservation: currentWeatherData, derivedLocationName: locationDisplayName }; 
    } else {
      // console.error('WeatherWidget: parseCurrentWeatherData - Unexpected data format or missing location. Data:', data);
      // setCurrentWeather(null); // Decide how to handle error - clear or set specific error state
      // setError('Could not parse current weather data.'); // Example
      throw new Error('Unexpected data format for current weather processing');
    }
  }, [setLocation, setCurrentWeather, setCurrentZipcode, formatTime]); // Added formatTime to deps
  
  // Parse daily forecast data
  const parseDailyForecastData = useCallback((data) => {
    // Add debugging to see what format we're getting
    // console.log('Raw data received by parseDailyForecastData:', JSON.stringify(data).substring(0, 200)); // Log start of stringified data

    // console.log('Checking data.forecast:', typeof data.forecast, data.forecast ? Object.keys(data.forecast).slice(0, 10) : 'undefined/null'); // Log type and first 10 keys

    // *** Use the nested forecast object if present, checking for double nesting ***
    const forecastPayload = data.forecast?.forecast || data.forecast || data;
    // **********************************************

    // console.log('Assigned forecastPayload keys:', typeof forecastPayload, Object.keys(forecastPayload).slice(0, 10)); // Log type and first 10 keys

    // console.log('Forecast data format (using payload):', {
    //   // Check the payload for expected fields
    //   hasCalendarTemp: Array.isArray(forecastPayload.calendarDayTemperatureMax),
    //   hasDayOfWeek: Array.isArray(forecastPayload.dayOfWeek),
    //   hasNarrative: Array.isArray(forecastPayload.narrative),
    //   hasDaypart: forecastPayload.daypart !== undefined,
    //   payloadKeys: Object.keys(forecastPayload)
    // });

    // Save the raw data for later use in detail views
    const rawForecastData = data; // Keep original raw data if needed elsewhere

    // *** Update checks to use forecastPayload ***
    if (forecastPayload && forecastPayload.dailyForecast) {
      // Data is already in the format we expect (unlikely based on logs, but keep for safety)
      return forecastPayload.dailyForecast; 
    } else if (forecastPayload && Array.isArray(forecastPayload.forecasts)) {
      // Map forecast data to our format (less likely TWC format)
      const mappedForecast = forecastPayload.forecasts.map(day => ({ /* ... mapping ... */ }));
      return mappedForecast; // Return the mapped data
    } else if (forecastPayload && 
               Array.isArray(forecastPayload.calendarDayTemperatureMax) && 
               Array.isArray(forecastPayload.calendarDayTemperatureMin)) {
      // console.log('PROCESSING NESTED FORECAST DATA FORMAT (calendarDay temps)');
      
      const dayNames = forecastPayload.dayOfWeek;
      const maxTemps = forecastPayload.calendarDayTemperatureMax;
      const minTemps = forecastPayload.calendarDayTemperatureMin || forecastPayload.temperatureMin;
      const narratives = forecastPayload.narrative || [];
      
      const daysCount = Math.min(dayNames.length, maxTemps.length, minTemps.length);
      const forecastItems = [];
      for (let i = 0; i < daysCount; i++) {
        let precipitation = '0%';
        let dayPrecipChance = 0;
        let precipData = {};
        let dayData = {};
        let nightData = {};
        
        if (forecastPayload.daypart && forecastPayload.daypart[0]) {
          const dayIndex = i * 2;
          const nightIndex = dayIndex + 1;
          let dayChance = 0;
          let nightChance = 0;
          
          if (forecastPayload.daypart[0].precipChance && Array.isArray(forecastPayload.daypart[0].precipChance)) {
            dayChance = forecastPayload.daypart[0].precipChance[dayIndex] !== undefined ? forecastPayload.daypart[0].precipChance[dayIndex] : 0;
            nightChance = forecastPayload.daypart[0].precipChance[nightIndex] !== undefined ? forecastPayload.daypart[0].precipChance[nightIndex] : 0;
          }
          dayPrecipChance = Math.max(dayChance || 0, nightChance || 0);
          precipitation = `${dayPrecipChance}%`;
          
          Object.keys(forecastPayload.daypart[0]).forEach(key => {
            if (Array.isArray(forecastPayload.daypart[0][key])) {
              if (forecastPayload.daypart[0][key][dayIndex] !== undefined) dayData[key] = forecastPayload.daypart[0][key][dayIndex];
              if (nightIndex < forecastPayload.daypart[0][key].length && forecastPayload.daypart[0][key][nightIndex] !== undefined) nightData[key] = forecastPayload.daypart[0][key][nightIndex];
            }
          });
          precipData.day = dayData;
          precipData.night = nightData;
        }
        
        if (forecastPayload.qpf && Array.isArray(forecastPayload.qpf)) precipData.qpf = forecastPayload.qpf[i];
        if (forecastPayload.qpfSnow && Array.isArray(forecastPayload.qpfSnow)) precipData.qpfSnow = forecastPayload.qpfSnow[i];
        
        const dayWxPhrase = dayData?.wxPhraseLong || narratives[i] || '';

        forecastItems.push({
          dayOfWeek: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayNames[i],
          temperature: {
            high: i === 0 && maxTemps[i] === null ? forecastPayload.temperatureMax?.[i] || 65 : maxTemps[i],
            low: minTemps[i]
          },
          condition: dayWxPhrase,
          precipitation: precipitation,
          precipChance: dayPrecipChance,
          precipData: precipData,
          narrative: narratives[i] || '',
          details: {
            day: dayData, 
            night: nightData, 
            sunrise: forecastPayload.sunriseTimeLocal ? forecastPayload.sunriseTimeLocal[i] : null,
            sunset: forecastPayload.sunsetTimeLocal ? forecastPayload.sunsetTimeLocal[i] : null,
            moonrise: forecastPayload.moonriseTimeLocal ? forecastPayload.moonriseTimeLocal[i] : null,
            moonset: forecastPayload.moonsetTimeLocal ? forecastPayload.moonsetTimeLocal[i] : null,
            moonPhase: forecastPayload.moonPhase ? forecastPayload.moonPhase[i] : null,
            qpf: forecastPayload.qpf ? forecastPayload.qpf[i] : 0,
            qpfSnow: forecastPayload.qpfSnow ? forecastPayload.qpfSnow[i] : 0
          },
          rawData: forecastPayload // Store the inner forecast object as raw data
        });
      }
      // console.log('Parsed nested forecast items correctly:', forecastItems);
      return forecastItems; // Return the parsed items
    } else if (forecastPayload && forecastPayload.dayOfWeek) {
      // console.log('PROCESSING STANDARD FORECAST DATA FORMAT (dayOfWeek)');
      const dayNames = forecastPayload.dayOfWeek;
      const maxTemps = forecastPayload.temperatureMax;
      const minTemps = forecastPayload.temperatureMin;
      const phrases = forecastPayload.wxPhraseLong || forecastPayload.narrative || [];
      const daysCount = Math.min(dayNames.length, maxTemps.length);
      const forecastItems = [];
      for (let i = 0; i < daysCount; i++) {
        forecastItems.push({
          dayOfWeek: i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : dayNames[i],
          temperature: { high: maxTemps[i], low: minTemps[i] },
          condition: phrases[i] || '',
          precipitation: forecastPayload.precipChance ? `${forecastPayload.precipChance[i]}%` : '0%',
          rawData: forecastPayload // Store inner forecast object
        });
      }
      return forecastItems; // Return the parsed items
    } else {
      // console.error('Unexpected data format for forecast payload:', forecastPayload);
      throw new Error('Unexpected data format for forecast');
    }
  }, []); // Assuming this function is pure or only uses stable setters implicitly
  
  // Parse hourly data
  const parseHourlyData = useCallback((data) => {
    // Create hourly forecast array from the API response
    const hourlyItems = [];
    
    // Log important fields to help with debugging
    // console.log('Hourly data structure:', {
    //   hasValidTimeLocal: Array.isArray(data.validTimeLocal),
    //   hasTemperature: Array.isArray(data.temperature),
    //   hasWindSpeed: Array.isArray(data.windSpeed),
    //   hasPrecipChance: Array.isArray(data.precipChance),
    //   hasWxPhrase: Array.isArray(data.wxPhraseLong) || Array.isArray(data.wxPhraseShort),
    //   timeCount: data.validTimeLocal?.length,
    //   fields: Object.keys(data)
    // });
    
    // If data is already in the expected format (from PHP cache), use it directly
    if (Array.isArray(data) && data.length > 0 && data[0].time) {
      // console.log('Using pre-formatted hourly data from cache');
      setHourlyForecast(data);
      return;
    }
    
    // Ensure we have the necessary data fields
    if (!data.validTimeLocal || !Array.isArray(data.validTimeLocal)) {
      throw new Error('Missing validTimeLocal field in hourly forecast data');
    }
    if (!data.temperature || !Array.isArray(data.temperature)) {
      throw new Error('Missing temperature field in hourly forecast data');
    }
    
    // Number of hours to display (12)
    const hoursToShow = Math.min(12, data.validTimeLocal.length);
    
    for (let i = 0; i < hoursToShow; i++) {
      try {
        const validTime = new Date(data.validTimeLocal[i]);
        const hour = validTime.getHours();
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const hour12 = hour % 12 || 12; // Convert to 12-hour format
        
        // Handle optional fields safely
        const windDirectionVal = data.windDirectionCardinal && Array.isArray(data.windDirectionCardinal) && data.windDirectionCardinal[i] 
                                ? data.windDirectionCardinal[i] + ' ' : '';
        
        const windSpeedVal = data.windSpeed && Array.isArray(data.windSpeed) && data.windSpeed[i] !== undefined 
                            ? data.windSpeed[i] + ' mph' : '';
        
        // Get weather condition from multiple possible fields
        let condition = '';
        if (data.wxPhraseShort && Array.isArray(data.wxPhraseShort) && data.wxPhraseShort[i]) {
          condition = data.wxPhraseShort[i];
        } else if (data.wxPhraseLong && Array.isArray(data.wxPhraseLong) && data.wxPhraseLong[i]) {
          condition = data.wxPhraseLong[i];
        }
        
        // Get precipitation chance if available
        const precipChance = data.precipChance && Array.isArray(data.precipChance) && data.precipChance[i] !== undefined
                            ? data.precipChance[i] + '%' : '0%';
        
        hourlyItems.push({
          time: `${hour12} ${ampm}`,
          temperature: data.temperature[i],
          condition: condition,
          wind: windDirectionVal + windSpeedVal,
          precipitation: precipChance,
          windDirection: data.windDirectionCardinal && Array.isArray(data.windDirectionCardinal) ? data.windDirectionCardinal[i] : null,
          windSpeed: data.windSpeed && Array.isArray(data.windSpeed) && data.windSpeed[i] !== undefined ? data.windSpeed[i] : 0,
          isDay: hour >= 6 && hour < 18, // Simple day/night detection based on hour
          
          // Additional detailed data
          humidity: data.relativeHumidity && Array.isArray(data.relativeHumidity) && data.relativeHumidity[i] !== undefined ? 
                    data.relativeHumidity[i] + '%' : '64%',
          
          heatIndex: data.temperatureHeatIndex && Array.isArray(data.temperatureHeatIndex) && data.temperatureHeatIndex[i] !== undefined ? 
                     data.temperatureHeatIndex[i] : data.temperature[i],
          
          windChill: data.temperatureWindChill && Array.isArray(data.temperatureWindChill) && data.temperatureWindChill[i] !== undefined ? 
                     data.temperatureWindChill[i] : data.temperature[i],
          
          dewPoint: data.temperatureDewPoint && Array.isArray(data.temperatureDewPoint) && data.temperatureDewPoint[i] !== undefined ? 
                    data.temperatureDewPoint[i] : null,
          
          uvIndex: data.uvIndex && Array.isArray(data.uvIndex) && data.uvIndex[i] !== undefined ? 
                   `${data.uvIndex[i]} ${data.uvIndex[i] <= 2 ? 'Low' : data.uvIndex[i] <= 5 ? 'Moderate' : data.uvIndex[i] <= 7 ? 'High' : data.uvIndex[i] <= 10 ? 'Very High' : 'Extreme'}` : '0 Low',
          
          visibility: data.visibility && Array.isArray(data.visibility) && data.visibility[i] !== undefined ? 
                      data.visibility[i] + ' mi' : '10 mi'
        });
      } catch (e) {
        // console.error('Error parsing hourly forecast item at index ' + i + ':', e);
      }
    }
    
    if (hourlyItems.length === 0) {
      throw new Error('Failed to parse any hourly forecast data items');
    }
    
    // console.log('Parsed hourly forecast items:', hourlyItems);
    setHourlyForecast(hourlyItems);
  }, [setHourlyForecast]); // setHourlyForecast is stable
  
  // Format time string
  const formatTime = (timeString) => {
    try {
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true 
      });
    } catch (e) {
      return timeString;
    }
  };
  
  // Submit location search
  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchZipcode) {
      // Gather current UI settings to pass to loadWeatherData
      const currentUiSettings = {
        showTabs: initialShowTabs, // from state
        showTodayForecast: showTodayForecast, // from state
        showCurrentWeather: showCurrentWeather, // from state
        showSearchBox: showSearchBox, // from state
        showLocationHeader: showLocationHeader, // from state
        showBorder: showBorder, // from state
        showAlertsUI: showAlertsUI // from state
      };
      // Call with searched zip to force bypass cache and pass current UI settings
      loadWeatherData(searchZipcode, currentUiSettings);
      
      // *** Update URL after initiating search ***
      if (typeof window !== 'undefined') {
        const url = new URL(window.location);
        if (url.searchParams.get('zip') !== searchZipcode) {
          url.searchParams.set('zip', searchZipcode);
          window.history.replaceState({}, '', url);
          // console.log('Updated URL with searched zipcode:', searchZipcode);
        }
      }
      // *************************************
    }
  };
  
  // Fetch today's forecast
  const fetchTodayForecast = useCallback(async (zipcode, bypassCache) => {
    try {
      // console.log(`Fetching today's forecast for ${zipcode}`);
      
      const forecastData = await getForecast(zipcode, 3);
      // console.log('Today\'s forecast data received:', forecastData);
      
      // Use the common parsing function
      const parsedItems = parseDailyForecastData(forecastData);
      
      if (parsedItems && parsedItems.length > 0) {
        const today = parsedItems[0]; // Get the first day's data
        const tomorrow = parsedItems.length > 2 ? parsedItems[2] : null; // Get day after tomorrow if available
        const tonightNarrative = today.details?.night?.narrative || today.details?.night?.wxPhraseLong || null;

        // Construct the object for setTodayForecast
        const todayForecastData = {
          description: today.narrative || today.details?.day?.narrative || today.condition, // Use overall narrative, day narrative, or condition
          tonight: tonightNarrative, 
          tomorrow: tomorrow ? (tomorrow.narrative || tomorrow.details?.day?.narrative || tomorrow.condition) : null
        };

        setTodayForecast(todayForecastData);
        // console.log('Today\'s forecast set using parsed data:', todayForecastData);

        // Return the full data in case the caller needs it (loadWeatherData doesn't, but good practice)
        return forecastData; 

      } else {
        // console.error('Failed to parse forecast data or no items returned');
        throw new Error('Could not extract today forecast data after parsing');
      }

    } catch (error) {
      // console.error('Error fetching or parsing today\'s forecast:', error);
      // Make sure state is cleared on error
      setTodayForecast(null);
      throw error; // Re-throw error so the calling function knows it failed
    }
  }, [parseDailyForecastData, setTodayForecast]);
  
  // Fetch hourly forecast
  const fetchHourlyForecast = useCallback(async (zipcode) => {
    try {
      const hourlyData = await getHourlyForecast(zipcode);
      // console.log('Hourly data received:', hourlyData);
      
      // Check if the data is already in the expected format (array of objects with time property)
      if (Array.isArray(hourlyData) && hourlyData.length > 0 && hourlyData[0].time) {
        // console.log('Using pre-formatted hourly data from API');
        setHourlyForecast(hourlyData);
        return hourlyData;
      }
      
      // Check if we have the direct hourly data format (with validTimeLocal array)
      if (hourlyData.validTimeLocal && Array.isArray(hourlyData.validTimeLocal)) {
        // Use the data directly since it already has the expected structure
        parseHourlyData(hourlyData);
        return hourlyData;
      } 
      // Check for the v3-wx-forecast format (fallback for old API format)
      else if (hourlyData['v3-wx-forecast-hourly-2day']) {
        const data = hourlyData['v3-wx-forecast-hourly-2day'];
        parseHourlyData(data);
        return data;
      } else {
        throw new Error('Unexpected format in hourly forecast response');
      }
    } catch (error) {
      // console.error('Error fetching hourly forecast:', error);
      throw error;
    }
  }, [parseHourlyData, setHourlyForecast]);
  
  // Fetch alerts data
  const fetchAlerts = useCallback(async (zipcode) => {
    try {
      setLoadingAlerts(true);
      const data = await getAlerts(zipcode);
      // console.log('Raw alerts data received:', data);
      
      // Check if data is valid
      if (!data) {
        // console.log('No valid alerts data received');
        return { alerts: [] }; // Return empty alerts structure
      }
      
      if (data && data.alerts) {
        // console.log('Alerts data structure:', {
        //   hasAlertsObject: typeof data.alerts === 'object',
        //   hasAlertsArray: data.alerts && Array.isArray(data.alerts.alerts),
        //   alertsCount: data.alerts && data.alerts.alerts ? data.alerts.alerts.length : 0
        // });
        
        // Structure: data.alerts.alerts[] 
        if (data.alerts.alerts && Array.isArray(data.alerts.alerts) && data.alerts.alerts.length > 0) {
          // console.log('Setting alerts from data.alerts');
          return data.alerts;
        } 
        // Structure: data.alerts.alertDetailInfo[]
        else if (data.alerts.alertDetailInfo && Array.isArray(data.alerts.alertDetailInfo)) {
          // console.log('Setting alerts from data.alerts.alertDetailInfo');
          return data.alerts;
        }
        // No alerts found
        else {
          // console.log('No alerts found in data.alerts');
          return { alerts: [] }; // Return empty alerts structure
        }
      } else if (data && Array.isArray(data.alertDetailInfo)) {
        // console.log('Setting alerts from root alertDetailInfo array');
        return { alertDetailInfo: data.alertDetailInfo };
      } else {
        // console.warn('Unexpected alerts data format:', data);
        return { alerts: [] }; // Return empty alerts structure
      }
    } catch (error) {
      // console.error('Error fetching alerts:', error);
      return { alerts: [] }; // Return empty alerts structure on error
    } finally {
      setLoadingAlerts(false);
    }
  }, [setLoadingAlerts]); // Removed setAlerts as it's returned
  
  // Fetch alert detail
  const fetchAlertDetail = useCallback(async (alertId) => {
    try {
      // console.log('Fetching alert detail for:', alertId);
      const data = await getAlertDetail(alertId);
      // console.log('Alert detail data:', data);
      
      if (data && data.alertDetail) {
        // console.log('Alert detail found with text info:', !!data.alertDetail.texts);
        // Update alerts with the detail data
        setAlerts(prevAlerts => {
          // If alerts are in the nested structure
          if (prevAlerts.alerts && Array.isArray(prevAlerts.alerts)) {
            // Keep the existing alerts structure but add the detail data
            return {
              ...prevAlerts,
              alertDetail: data.alertDetail
            };
          } 
          // If alerts are in the root level
          else if (Array.isArray(prevAlerts)) {
            // Add the detail to the top level
            return {
              alerts: prevAlerts,
              alertDetail: data.alertDetail
            };
          }
          // Otherwise just add the alertDetail
          return {
            ...prevAlerts,
            alertDetail: data.alertDetail
          };
        });
        
        // Also keep a separate copy if needed
        setAlertDetail(data.alertDetail);
      } else {
        // console.warn('Unexpected alert detail format:', data);
      }
    } catch (error) {
      // console.error('Error fetching alert detail:', error);
    }
  }, [setAlerts, setAlertDetail]);

  // ===================================================================================
  // DEBUG HELPER to find cause of infinite loop
  // ===================================================================================
  
  // Load weather data
  const loadWeatherData = useCallback(async (zipcodeToLoad, settingsForRun) => {
    const forceLoad = !!zipcodeToLoad; 
    const zipToUse = zipcodeToLoad; // Directly use the passed zipcode

    // console.log(`loadWeatherData called for: ${zipToUse}, Force Load: ${forceLoad}, Settings:`, settingsForRun);

    if (forceLoad) {
      setLoading(true);
    }
    setError(null);

    // Reset states only if this is a new search initiated by the user (forceLoad implies this here)
    if (forceLoad) {
      // console.log(`New user search or forced load for [${zipToUse}], resetting states.`);
      setCurrentWeather(null);
      setHourlyForecast(null);
      setForecast(null);
      setTodayForecast(null);
      setAlerts(null);
      setAlertDetail(null);
    }

    try {
      // UI settings are now passed via settingsForRun
      const settings = settingsForRun;

      window.ohtWeatherCachedData = window.ohtWeatherCachedData || {};
      const currentZipToProcess = zipToUse;
      const bypassCache = forceLoad; // If zipcodeToLoad is provided, it implies a bypass or specific load

      if (!bypassCache) {
        // Attempt to populate from cache immediately if not forcing load (e.g. initial non-preloaded state)
        // console.log('Attempting to populate states from existing cache (if any)...');
        if (window.ohtWeatherCachedData.current) setCurrentWeather(window.ohtWeatherCachedData.current);
        if (window.ohtWeatherCachedData.hourly) setHourlyForecast(window.ohtWeatherCachedData.hourly);
        if (window.ohtWeatherCachedData.forecast) {
          const parsedForecast = parseDailyForecastData(window.ohtWeatherCachedData.forecast);
          if (parsedForecast) setForecast(parsedForecast);
          try {
            if (parsedForecast && parsedForecast.length > 0) {
              const today = parsedForecast[0]; 
              const tomorrow = parsedForecast.length > 2 ? parsedForecast[2] : null; 
              const tonightNarrative = today.details?.night?.narrative || today.details?.night?.wxPhraseLong || null;
              setTodayForecast({
                description: today.narrative || today.details?.day?.narrative || today.condition, 
                tonight: tonightNarrative, 
                tomorrow: tomorrow ? (tomorrow.narrative || tomorrow.details?.day?.narrative || tomorrow.condition) : null
              });
            }
          } catch (e) { console.error('Error setting today forecast from cache', e); }
        }
        if (window.ohtWeatherCachedData.alerts) setAlerts(window.ohtWeatherCachedData.alerts);
        setLoading(false); 
      }

      let overallLoading = false;
      let promises = [];

      if (settings.showAlertsUI) {
        if (bypassCache || !window.ohtWeatherCachedData.alerts) {
          overallLoading = true;
          promises.push(
            fetchAlerts(currentZipToProcess)
              .then(alertsData => {
                if (alertsData) {
                  window.ohtWeatherCachedData.alerts = alertsData;
                  setAlerts(alertsData);
                }
              })
              .catch(err => { 
                  // console.error('Alerts fetch failed:', err); 
                  setAlerts({ alerts: [] }); 
                  window.ohtWeatherCachedData.alerts = { alerts: [] };
               })
          );
        }
      } else {
        setAlerts(null);
      }
      
      if (settings.showCurrentWeather) {
          // const cachedCurrent = !bypassCache ? window.ohtWeatherCachedData.current : null; // Removed complex cache check here
          // const hasValidParsedCache = cachedCurrent && cachedCurrent.temperature !== undefined;
          // const hasValidRawCache = cachedCurrent && cachedCurrent.current && cachedCurrent.current.temperature !== undefined;

          // if (bypassCache || !(hasValidParsedCache || hasValidRawCache)) { // Simplified: loadWeatherData is called when we need to fetch
              overallLoading = true;
              promises.push(
                getCurrentWeather(currentZipToProcess)
                    .then(currentDataFromApi => {
                        if (currentDataFromApi && (currentDataFromApi.current || currentDataFromApi.observation)) {
                            const { parsedObservation, derivedLocationName } = parseCurrentWeatherData(currentDataFromApi, currentZipToProcess);
                            setCurrentWeather(parsedObservation);
                            setLocation(derivedLocationName); // Update display name from API for this fetch
                            if (typeof window !== 'undefined' && window.localStorage) {
                                localStorage.setItem('weather_location', derivedLocationName);
                                localStorage.setItem('last_weather_zipcode', currentZipToProcess);
                            }
                        } else {
                            // console.error('Invalid data format for current weather API.');
                            setCurrentWeather(null);
                            // Potentially set location to an error or zipcode if API fails badly
                            setLocation(currentZipToProcess + ' (Error)');
                        }
                    })
                    .catch(err => {
                        // console.error('Error loading current weather:', err);
                        setCurrentWeather(null);
                        setLocation(currentZipToProcess + ' (Error)');
                    })
              );
          // } else if (!bypassCache) { // Removed this block
          //     if (hasValidRawCache) {
          //       const { parsedObservation, derivedLocationName } = parseCurrentWeatherData(cachedCurrent, zipToUse);
          //       setCurrentWeather(parsedObservation);
          //       // setLocation(derivedLocationName); // DO NOT DO THIS HERE if using this old cache path
          //     } else if (hasValidParsedCache) {
          //       setCurrentWeather(cachedCurrent);
          //     }
          // }
      } else {
          setCurrentWeather(null);
      }

      if (settings.showTodayForecast) {
          const cachedForecastForToday = !bypassCache ? window.ohtWeatherCachedData.forecast : null;
          if (bypassCache || !cachedForecastForToday) {
              overallLoading = true; 
              promises.push(
                fetchTodayForecast(currentZipToProcess, bypassCache)
                    .then(forecastData => {
                        if (forecastData) {
                            window.ohtWeatherCachedData.forecast = forecastData; 
                        }
                    })
                    .catch(err => {
                        // console.error('Today forecast failed:', err);
                        setTodayForecast(null);
                    })
              );
          }
      } else {
          setTodayForecast(null);
      }

      if (settings.showTabs.includes('hourly')) {
          const cachedHourly = !bypassCache ? window.ohtWeatherCachedData.hourly : null;
          if (bypassCache || !cachedHourly || cachedHourly.length === 0) {
              overallLoading = true;
              promises.push(
                fetchHourlyForecast(currentZipToProcess)
                    .catch(err => {
                        // console.error('Hourly forecast failed:', err);
                        setHourlyForecast(null);
                    })
              );
          }
      } else {
          setHourlyForecast(null);
      }

      if (settings.showTabs.includes('upcoming')) {
        const cachedForecast = !bypassCache ? window.ohtWeatherCachedData.forecast : null;
        if (bypassCache || !cachedForecast || !cachedForecast.forecast?.calendarDayTemperatureMax || cachedForecast.forecast.calendarDayTemperatureMax.length < 7) {
            overallLoading = true;
            promises.push(
              getForecast(currentZipToProcess, 7)
                  .then(forecastData => {
                      if (forecastData) {
                          window.ohtWeatherCachedData.forecast = forecastData;
                          const parsedItems = parseDailyForecastData(forecastData);
                          if (parsedItems) setForecast(parsedItems);
                          else setForecast(null);
                      } else {
                          setForecast(null);
                      }
                  })
                  .catch(err => {
                      // console.error('Error loading 7-day forecast:', err);
                      setForecast(null);
                  })
            );
        }
      } else {
        setForecast(null);
      }

      if (overallLoading) {
        if(!loading) setLoading(true); // Ensure loading is true if we are making API calls
        await Promise.all(promises);
      }
      // Always set loading to false after all attempts, regardless of overallLoading initially
      // This handles cases where we might have used some cache but still fetched other parts.
      setLoading(false);

    } catch (error) {
      // console.error('Error loading weather data:', error);
      setError(error.message || 'Failed to load weather data');
      setLoading(false);
    }
  }, [
    // Keep only stable functions and setters that loadWeatherData *itself* depends on
    // Do NOT include currentZipcode or any of the show... UI states here.
    parseCurrentWeatherData, 
    fetchTodayForecast, 
    fetchHourlyForecast, 
    fetchAlerts,
    parseDailyForecastData,
    setLoading, 
    setError, 
    setCurrentWeather, 
    setHourlyForecast, 
    setForecast, 
    setTodayForecast, 
    setAlerts, 
    setAlertDetail
    // Imported API functions like getCurrentWeather, getForecast are stable by default
  ]);
  
  // Main data loading effect
  useEffect(() => {
    // Check for a zip code in the URL first.
    const urlParams = new URLSearchParams(window.location.search);
    const zipFromUrl = urlParams.get('zip');

    const locationData = {
      zipcode: zipFromUrl || initialZipcode, // Prioritize zip from URL
      locationName: initialLocationName,
      showTabs: initialShowTabs,
      showTodayForecast: initialShowTodayForecast,
      showCurrentWeather: initialShowCurrentWeather,
      showSearchBox: initialShowSearchBox,
      showLocation: initialShowLocation,
      showBorder: initialShowBorder,
      showAlerts: initialShowAlerts,
      linkUrl: initialLinkUrl
    };
    const instanceZip = locationData.zipcode;
    
    let initialDisplayName = locationData.locationName;
    // Check if initialDisplayName is empty, just a comma, or only whitespace
    if (!initialDisplayName || initialDisplayName.trim() === '' || initialDisplayName.trim() === ',') {
        initialDisplayName = instanceZip || 'Loading...'; // Fallback to zipcode or a generic loading text
    }
    const attributeLocationName = initialDisplayName;

    setCurrentZipcode(instanceZip);
    // console.log(`WeatherWidget (useEffect) - About to setLocation with: '${attributeLocationName}'`);
    setLocation(attributeLocationName);
    if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem('weather_location', attributeLocationName);
        localStorage.setItem('weather_zipcode', instanceZip);
    }

    const uiSettings = {
        showTabs: locationData.showTabs || ['hourly', 'map', 'upcoming'],
        showTodayForecast: locationData.showTodayForecast !== undefined ? locationData.showTodayForecast : true,
        showCurrentWeather: locationData.showCurrentWeather !== undefined ? locationData.showCurrentWeather : true,
        showSearchBox: locationData.showSearchBox !== undefined ? locationData.showSearchBox : true,
        showLocationHeader: locationData.showLocation !== undefined ? locationData.showLocation : true,
        showBorder: locationData.showBorder !== undefined ? locationData.showBorder : true,
        showAlertsUI: locationData.showAlerts !== undefined ? locationData.showAlerts : true,
        linkUrl: locationData.linkUrl || ''
    };

    setShowTodayForecast(uiSettings.showTodayForecast);
    setShowCurrentWeather(uiSettings.showCurrentWeather);
    setShowSearchBox(uiSettings.showSearchBox);
    setShowLocationHeader(uiSettings.showLocationHeader);
    setShowBorder(uiSettings.showBorder);
    setShowAlertsUI(uiSettings.showAlertsUI);
    setLinkUrl(uiSettings.linkUrl);

    // This is the key change: If a specific zipcode is passed via props, always use it to load data.
    if (instanceZip) {
      // console.log(`WeatherWidget (useEffect): A specific zipcode (${instanceZip}) was provided, calling loadWeatherData.`);
      loadWeatherData(instanceZip, uiSettings);
    } else {
      // Fallback logic for when no zipcode is provided (e.g., legacy shortcode with no zip)
      const canUsePreloaded =
        initialGlobalCachedData?.settings?.preload_main_widget === '1' &&
        initialGlobalCachedData?.current;

      if (canUsePreloaded) {
        // console.log(`WeatherWidget (useEffect): Using preloaded data for default zipcode.`);
        const preloadedZip = initialGlobalCachedData.default_zipcode_for_preload;
        
        const { hourly: preloadedHourly, forecast: preloadedForecastPayload, alerts: preloadedAlertsPayload } = initialGlobalCachedData;
        const currentDataForParsing = initialGlobalCachedData.current;

        if (currentDataForParsing && currentDataForParsing.current) {
          parseCurrentWeatherData(currentDataForParsing, preloadedZip);
        }

        if (preloadedHourly) {
          setHourlyForecast(preloadedHourly);
        }

        if (preloadedForecastPayload) {
          const parsedPreloadedForecast = parseDailyForecastData({ forecast: preloadedForecastPayload });
          setForecast(parsedPreloadedForecast);
          if (parsedPreloadedForecast && parsedPreloadedForecast.length > 0) {
            const today = parsedPreloadedForecast[0];
            const tomorrow = parsedPreloadedForecast.length > 2 ? parsedPreloadedForecast[2] : null;
            const tonightNarrative = today.details?.night?.narrative || today.details?.night?.wxPhraseLong || null;
            setTodayForecast({
              description: today.narrative || today.details?.day?.narrative || today.condition,
              tonight: tonightNarrative,
              tomorrow: tomorrow ? (tomorrow.narrative || tomorrow.details?.day?.narrative || tomorrow.condition) : null
            });
          }
        }
        if (preloadedAlertsPayload) {
          setAlerts(preloadedAlertsPayload);
        }

        setLoading(false);
        setError(null);
      } else {
        // console.warn('WeatherWidget (useEffect): No instanceZip provided and preloaded data not available/applicable. Cannot load weather data.');
        setError('Cannot determine location zipcode.');
        setLoading(false);
      }
    }
  }, [
    initialZipcode, initialLocationName, initialShowTabs, 
    initialShowTodayForecast, initialShowCurrentWeather, 
    initialShowSearchBox, initialShowLocation, initialShowBorder, 
    initialShowAlerts, initialLinkUrl,
    loadWeatherData, parseCurrentWeatherData, parseDailyForecastData 
  ]);
  
  // Initialize map when map tab is activated
  useEffect(() => {
    if (activeTab === 'map' && !mapLoaded && weatherMapRef.current) {
      // Initialize map logic would go here
      setMapLoaded(true);
    }
  }, [activeTab, mapLoaded]);
  
  // Handle alert click to fetch detail
  const handleAlertClick = (alertId) => {
    fetchAlertDetail(alertId);
  };
  
  const handleHeaderClick = () => {
    if (linkUrl) {
      window.location.href = linkUrl;
    }
  };

  const LocationHeader = ({children}) => {
    if (linkUrl) {
      return (
        <a href={linkUrl} onClick={(e) => { e.preventDefault(); handleHeaderClick(); }} className="weather-header-link">
          {children}
        </a>
      );
    }
    return <>{children}</>;
  };

  return (
    <div 
      className={`oht-weather-widget ${!showBorder ? 'no-border' : ''}`} 
      data-loading={loading}
      ref={weatherRootRef}
    >
      {loading ? (
        <div className="weather-loading">
          <div className="loading-spinner"></div>
          <p>Loading weather data...</p>
        </div>
      ) : error ? (
        <div className="weather-error">
          <p>Error: {error}</p>
        </div>
      ) : (
        <div className="weather-content">

          <div className="weather-header">
            {/* Weather Header with Location - conditionally rendered based on showLocationHeader */}
            {showLocationHeader && (
              <LocationHeader>
                  <div className={`location ${linkUrl ? 'weather-header-link' : ''}`} onClick={handleHeaderClick}>
                    <span className="location-pin"><FontAwesomeIcon icon={faLocationDot} /></span>
                    <h2>{location}</h2>
                  </div>
                
              </LocationHeader>
            )}

            {/* Search Form - Now positioned absolutely relative to the header container */}
            {showSearchBox && (
              <div className="weather-search">
                <form onSubmit={handleSubmit}>
                  <input 
                    type="text" 
                    value={searchZipcode} 
                    onChange={(e) => setSearchZipcode(e.target.value)} 
                    placeholder="Enter ZIP code" 
                  />
                  <button type="submit">Go</button>
                </form>
              </div>
            )}
          </div>
          {/* Current Weather - conditionally rendered based on showCurrentWeather */}
          {showCurrentWeather && currentWeather && <WeatherCurrent weather={currentWeather} todayForecast={showTodayForecast ? todayForecast : null} />}
          
          {/* Weather Alerts Section - conditionally rendered based on showAlertsUI */}
          {showAlertsUI && alerts && <WeatherAlerts alerts={alerts} onAlertClick={handleAlertClick} />}
          
          {/* Weather Tabs */}
          <div className="weather-tabs">
            <div className="tab-buttons">
              {initialShowTabs.includes('hourly') && (
                <button 
                  className={activeTab === 'hourly' ? 'active' : ''} 
                  onClick={() => setUserSelectedTab('hourly')}
                >
                  Hourly
                </button>
              )}
              {initialShowTabs.includes('map') && (
                <button 
                  className={activeTab === 'map' ? 'active' : ''} 
                  onClick={() => setUserSelectedTab('map')}
                >
                  Map
                </button>
              )}
              {initialShowTabs.includes('upcoming') && (
                <button 
                  className={activeTab === 'upcoming' ? 'active' : ''} 
                  onClick={() => setUserSelectedTab('upcoming')}
                >
                  7 Day
                </button>
              )} 
            </div>
            
            {/* Tab Content */}
            {activeTab === 'hourly' && hourlyForecast ? (
              <HourlyForecast forecast={hourlyForecast} />
            ) : activeTab === 'hourly' ? (
              <div className="tab-content hourly-tab loading-spinner-container">
                <div className="loading-spinner"></div>
              </div>
            ) : null}
            
            {activeTab === 'map' && (
              <WeatherMap ref={weatherMapRef} isActive={activeTab === 'map'} zipcode={currentZipcode} />
            )}
            
            {activeTab === 'upcoming' && forecast ? (
              <WeatherForecast forecast={forecast} />
            ) : activeTab === 'upcoming' ? (
              <div className="tab-content forecast-tab loading-spinner-container">
                <div className="loading-spinner"></div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherWidget; 